import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { login } from "utils/auth";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import AuthShell, {
  PasswordInput,
  PasswordMeter,
  go,
  goAfterAuth,
  readableError,
} from "./auth/AuthShell";

const SIGNUP = gql`
  mutation shopAdminSignUp(
    $fullName: String!
    $email: String!
    $password: String!
    $phoneNumber: String!
    $phoneSignUpCode: String!
    $reffererCode: String
  ) {
    shopAdminSignUp(
      input: {
        fullName: $fullName
        email: $email
        password: $password
        phoneNumber: $phoneNumber
        phoneSignUpCode: $phoneSignUpCode
        refferalCode: $reffererCode
      }
    ) {
      _id
      fullName
      email
    }
  }
`;

/* Signing up returns the new admin but no token, so the account is
   signed in with the credentials just chosen rather than sending
   someone who typed a password ten seconds ago back to type it again. */
const LOGIN = gql`
  mutation adminLogin($emailOrPhoneNumber: String!, $password: String!) {
    adminLogin(input: { emailOrPhoneNumber: $emailOrPhoneNumber, password: $password }) {
      type
      token {
        accessToken
        refreshToken
      }
    }
  }
`;

const CHECK_EMAIL = gql`
  query adminCheckEmail($email: String!) {
    adminCheckEmail(email: $email) {
      exist
    }
  }
`;

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/**
 * Signup - the account details half of creating a shop admin.
 *
 * The phone number is already verified by the time anyone gets here;
 * this collects the rest and hands over to the shop form.
 */
const Signup = (props) => {
  const history = props.history;
  const phoneNumber = window.localStorage.getItem("phoneNumber");
  const signupCode = window.localStorage.getItem("signupCode");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referral, setReferral] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(false);

  // The email the last availability check was asked about, so a result
  // is only shown while it still describes what is in the field.
  const [checkedEmail, setCheckedEmail] = useState("");

  const [shopAdminSignUp] = useMutation(SIGNUP);
  const [adminLogin] = useMutation(LOGIN);
  const [checkEmail, { data: emailData, loading: emailChecking }] = useLazyQuery(CHECK_EMAIL, {
    fetchPolicy: "network-only",
  });

  // "admin already exists" used to arrive as a modal after the whole
  // form had been filled in and submitted, without saying which of the
  // email or the phone number was the problem.
  useEffect(() => {
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) return undefined;

    const timer = setTimeout(() => {
      setCheckedEmail(value);
      checkEmail({ variables: { email: value } });
    }, 500);
    return () => clearTimeout(timer);
  }, [email]);

  const emailResultIsCurrent = checkedEmail !== "" && checkedEmail === email.trim();
  const emailTaken =
    emailResultIsCurrent && emailData && emailData.adminCheckEmail && emailData.adminCheckEmail.exist;
  const emailFree =
    emailResultIsCurrent &&
    emailData &&
    emailData.adminCheckEmail &&
    !emailData.adminCheckEmail.exist;

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = "Enter your first name";
    if (!lastName.trim()) next.lastName = "Enter your last name";

    if (!email.trim()) next.email = "Enter your email";
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = "That does not look like an email";
    else if (emailTaken) next.email = "An account already uses this email";

    if (!password) next.password = "Choose a password";
    else if (password.length < 8) next.password = "Use at least 8 characters";

    if (!confirmPassword) next.confirmPassword = "Repeat your password";
    else if (password !== confirmPassword) next.confirmPassword = "The two passwords do not match";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;

    setFormError("");
    if (!validate()) return;

    const trimmedEmail = email.trim();
    let accountCreated = false;

    setSaving(true);
    shopAdminSignUp({
      variables: {
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        email: trimmedEmail,
        password,
        phoneNumber,
        phoneSignUpCode: signupCode,
        reffererCode: referral.trim() || null,
      },
    })
      .then(() => {
        accountCreated = true;
        return adminLogin({ variables: { emailOrPhoneNumber: trimmedEmail, password } });
      })
      .then((res) => {
        const admin = res.data && res.data.adminLogin;
        if (!admin || !admin.token) throw new Error("signed up but not signed in");

        login(admin.token.accessToken.toString(), admin.token.refreshToken.toString());

        const storage = window.localStorage;
        storage.setItem("permissions", JSON.stringify([]));
        storage.removeItem("shopId");
        storage.removeItem("shopType");
        storage.removeItem("signupCode");
        storage.removeItem("phoneNumber");
        storage.setItem("nextStep", "/addShop");

        goAfterAuth("/rules");
      })
      .catch((error) => {
        setSaving(false);
        // Failing after the account exists is not a failed sign-up —
        // saying so would invite a second attempt that cannot succeed.
        if (accountCreated) {
          window.localStorage.removeItem("signupCode");
          window.localStorage.removeItem("phoneNumber");
          setCreated(true);
          return;
        }
        setFormError(readableError(error));
      });
  };

  // The sign-up code is single use and expires; without it there is
  // nothing to submit against.
  if (!phoneNumber || !signupCode) {
    return (
      <AuthShell
        title="Start again"
        subtitle="We could not confirm your phone number."
        currentStep={0}
      >
        <div className="authForm">
          <div className="dsFormMessage warning" role="alert">
            <ErrorOutlineIcon />
            <span>
              Your verification has expired or was completed elsewhere. Enter your phone number
              again to continue.
            </span>
          </div>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => go(history, "/login?tab=signup")}
          >
            Back to sign up
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (created) {
    return (
      <AuthShell
        title="Your account is ready"
        subtitle="Sign in to add your shop and start taking orders."
        currentStep={1}
      >
        <div className="authForm">
          <div className="dsFormMessage success" role="status">
            <CheckCircleOutlineIcon />
            <span>We created your account but could not sign you in automatically.</span>
          </div>
          <Button variant="primary" size="lg" block onClick={() => go(history, "/login")}>
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="A few details"
      subtitle="This is the account you will sign in with."
      currentStep={0}
    >
      <form className="authForm" onSubmit={onSubmit}>
        <div className="authRecap">
          <span className="authRecapValue">{phoneNumber}</span>
          <span className="authInline ok">
            <CheckCircleOutlineIcon />
            Verified
          </span>
        </div>

        {formError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{formError}</span>
          </div>
        )}

        <div className="authRow">
          <Field label="First name" htmlFor="signupFirstName" error={errors.firstName} required>
            <input
              id="signupFirstName"
              className="dsInput"
              type="text"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </Field>

          <Field label="Last name" htmlFor="signupLastName" error={errors.lastName} required>
            <input
              id="signupLastName"
              className="dsInput"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Email"
          htmlFor="signupEmail"
          required
          error={errors.email || (emailTaken ? "An account already uses this email" : "")}
          hint={
            emailChecking
              ? "Checking whether this email is free"
              : emailFree
              ? "This email is free"
              : undefined
          }
        >
          <input
            id="signupEmail"
            className="dsInput"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Password" htmlFor="signupPassword" error={errors.password} required>
          <PasswordInput
            id="signupPassword"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {password.length > 0 && <PasswordMeter password={password} />}

        <Field
          label="Confirm password"
          htmlFor="signupConfirm"
          error={errors.confirmPassword}
          required
        >
          <PasswordInput
            id="signupConfirm"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </Field>

        <Field
          label="Referral code"
          htmlFor="signupReferral"
          optional
          hint="If someone invited you to Spark, put their code here."
        >
          <input
            id="signupReferral"
            className="dsInput"
            type="text"
            value={referral}
            onChange={(event) => setReferral(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" block loading={saving}>
          {saving ? "Creating your account" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Signup;
